import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const box = entry.borderBoxSize?.[0] || entry.borderBoxSize;
    const roundedHeaderHeight = Math.round(box?.blockSize ?? entry.target.getBoundingClientRect().height);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
    this.#syncPanelGeometry();

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  #syncPanelGeometry = () => {
    const columns = this.querySelector('.header__row--top .header__columns');
    if (!columns) return;
    const rect = columns.getBoundingClientRect();
    const headerGroup = this.closest('#header-group');
    const topHeaderInner = headerGroup?.querySelector('.voicsky-top-header__inner');
    const headerSection = this.closest('.header-section');
    const headerGroupTop = headerGroup?.getBoundingClientRect().top ?? 0;
    const headerSectionTop = headerSection?.getBoundingClientRect().top ?? 0;
    document.body.style.setProperty('--voicsky-header-columns-width', `${Math.round(rect.width)}px`);
    document.body.style.setProperty('--voicsky-header-columns-left', `${Math.round(rect.left)}px`);
    document.body.style.setProperty('--voicsky-header-columns-height', `${Math.round(rect.height)}px`);
    document.body.style.setProperty('--voicsky-header-columns-top', `${Math.round(rect.top)}px`);
    document.body.style.setProperty('--voicsky-header-columns-bottom', `${Math.round(rect.bottom)}px`);
    document.body.style.setProperty('--voicsky-header-columns-offset', `${Math.round(rect.top - headerSectionTop)}px`);
    if (topHeaderInner instanceof HTMLElement) {
      const topHeaderRect = topHeaderInner.getBoundingClientRect();
      const topHeaderStyles = getComputedStyle(topHeaderInner);
      const paddingLeft = Number.parseFloat(topHeaderStyles.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(topHeaderStyles.paddingRight) || 0;
      const contentLeft = topHeaderRect.left + paddingLeft;
      const contentWidth = Math.max(0, topHeaderRect.width - paddingLeft - paddingRight);
      document.body.style.setProperty('--voicsky-top-header-content-left', `${Math.round(contentLeft)}px`);
      document.body.style.setProperty('--voicsky-top-header-content-width', `${Math.round(contentWidth)}px`);
    } else {
      document.body.style.removeProperty('--voicsky-top-header-content-left');
      document.body.style.removeProperty('--voicsky-top-header-content-width');
    }
    headerGroup?.style.setProperty('--voicsky-header-columns-bottom-local', `${Math.round(rect.bottom - headerGroupTop)}px`);
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    if (stickyMode) this.#observeStickyPosition(stickyMode === 'always');
    this.#lastScrollTop = getScrollTop();
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #restoreScrolledMenu = (event) => {
    if (event.type === 'pointermove' && !event.movementX && !event.movementY) return;
    delete this.dataset.menuScrollDismissed;
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) {
      const scrollTop = getScrollTop();
      if (scrollTop > this.#lastScrollTop + 1) this.dataset.menuScrollDismissed = 'true';
      this.#lastScrollTop = scrollTop;
      return;
    }
    this.#syncPanelGeometry();
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const isStandalone =
      window.navigator.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches ||
      document.documentElement.classList.contains('voicsky-ios-standalone');
    const directionThreshold = isStandalone ? 6 : 1;
    const scrollDelta = scrollTop - this.#lastScrollTop;
    const isAtTop = scrollTop <= 1;

    // iOS standalone mode can report a few alternating scroll pixels while
    // momentum scrolling settles. Do not let that visual-viewport jitter
    // repeatedly toggle the scroll-up header between active and idle.
    if (!isAtTop && Math.abs(scrollDelta) < directionThreshold) return;

    const isScrollingUp = scrollDelta < 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.addEventListener('resize', this.#syncPanelGeometry);
    this.addEventListener('pointerdown', this.#syncPanelGeometry);
    this.addEventListener('pointerover', this.#syncPanelGeometry);
    requestAnimationFrame(this.#syncPanelGeometry);
    document.dispatchEvent(new CustomEvent('voicsky:header-ready'));

    const stickyMode = this.getAttribute('sticky');
    this.#lastScrollTop = getScrollTop();
    if (stickyMode) this.#observeStickyPosition(stickyMode === 'always');
    this.#scrollContainer = getScrollEventTarget();
    this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    this.addEventListener('pointermove', this.#restoreScrolledMenu);
    this.addEventListener('pointerdown', this.#restoreScrolledMenu);
    this.addEventListener('focusin', this.#restoreScrolledMenu);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('pointermove', this.#restoreScrolledMenu);
    this.removeEventListener('pointerdown', this.#restoreScrolledMenu);
    this.removeEventListener('focusin', this.#restoreScrolledMenu);
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.removeEventListener('resize', this.#syncPanelGeometry);
    this.removeEventListener('pointerdown', this.#syncPanelGeometry);
    this.removeEventListener('pointerover', this.#syncPanelGeometry);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
    document.body.style.removeProperty('--voicsky-header-columns-width');
    document.body.style.removeProperty('--voicsky-header-columns-left');
    document.body.style.removeProperty('--voicsky-header-columns-height');
    document.body.style.removeProperty('--voicsky-header-columns-top');
    document.body.style.removeProperty('--voicsky-header-columns-bottom');
    document.body.style.removeProperty('--voicsky-header-columns-offset');
    document.body.style.removeProperty('--voicsky-top-header-content-left');
    document.body.style.removeProperty('--voicsky-top-header-content-width');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target instanceof HTMLElement &&
          entry.target.querySelector(':scope > .voicsky-mega-panel')
        ) {
          return totalHeight;
        }
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          const box = entry.borderBoxSize?.[0] || entry.borderBoxSize;
          return totalHeight + (box?.blockSize ?? entry.target.getBoundingClientRect().height);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (
        element instanceof HTMLElement &&
        !element.querySelector(':scope > .voicsky-mega-panel')
      ) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (
              element instanceof HTMLElement &&
              !element.querySelector(':scope > .voicsky-mega-panel')
            ) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
